import type { ReportGranularity } from '@six-pockets/shared';
import { formatDayLabel, fromDateKey, toDateKey, todayKey } from '@/utils/date';

const pad = (n: number) => String(n).padStart(2, '0');

/** YYYY-MM of a date, in local time. */
export const toMonthKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

export function fromMonthKey(key: string): Date {
  const [y, m] = key.split('-').map(Number);
  return new Date(y!, m! - 1, 1);
}

export const thisMonthKey = () => toMonthKey(new Date());

/** Today, or this month: the default selection. */
export const currentKey = (g: ReportGranularity) => (g === 'day' ? todayKey() : thisMonthKey());

/** The key `n` days or months after `key`. */
export function shiftKey(g: ReportGranularity, key: string, n: number): string {
  if (g === 'day') {
    const d = fromDateKey(key);
    return toDateKey(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n));
  }
  const d = fromMonthKey(key);
  return toMonthKey(new Date(d.getFullYear(), d.getMonth() + n, 1));
}

/** The longest range the server allows (366 days or 60 months), as the furthest key from `key`. */
export const MAX_SPAN: Record<ReportGranularity, number> = { day: 365, month: 59 };

const thisYear = () => new Date().getFullYear();

/** "5 Oct" (with the year when it isn't this year), or "Oct 2026". */
export function periodLabel(g: ReportGranularity, key: string): string {
  if (g === 'month') return fromMonthKey(key).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  const d = fromDateKey(key);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(d.getFullYear() !== thisYear() && { year: 'numeric' }) });
}

/** The selection in words: "Today", "Sat, 3 Oct", "1 Oct → 5 Oct", "October 2026", "Jan 2026 → Oct 2026". */
export function selectionLabel(g: ReportGranularity, from: string, to: string): string {
  if (from === to) {
    if (g === 'day') return formatDayLabel(from);
    return from === thisMonthKey() ? 'This month' : fromMonthKey(from).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }
  return `${periodLabel(g, from)} → ${periodLabel(g, to)}`;
}

/** The selection as a phrase for card captions: "today", "on 3 Oct", "in October 2026", "from 1 Oct to 5 Oct". */
export function selectionPhrase(g: ReportGranularity, from: string, to: string): string {
  if (from !== to) return `from ${periodLabel(g, from)} to ${periodLabel(g, to)}`;
  if (g === 'day') return from === todayKey() ? 'today' : `on ${periodLabel(g, from)}`;
  return from === thisMonthKey() ? 'this month' : `in ${selectionLabel(g, from, to)}`;
}

/** Axis money: ₹800, ₹12K, ₹1.5L, ₹2Cr. */
export function compactINR(n: number): string {
  const trim = (v: number) => String(Math.round(v * 10) / 10);
  if (n >= 1e7) return `₹${trim(n / 1e7)}Cr`;
  if (n >= 1e5) return `₹${trim(n / 1e5)}L`;
  if (n >= 1e3) return `₹${trim(n / 1e3)}K`;
  return `₹${Math.round(n)}`;
}

/** Round axis ticks from 0 that cover `max`: steps of 1, 2, 2.5 or 5 × a power of ten. */
export function niceTicks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw)!;
  const ticks: number[] = [];
  for (let v = 0; v < max + step; v += step) ticks.push(v);
  return ticks;
}

/** "42%" of a whole, or "—" when the whole is 0. */
export function share(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : '—';
}
